/**
 * ReactifFormModal — create OR edit a reagent (full CRUD, L-1: edit is a PATCH, never a destructive
 * rewrite). Keyboard-first (autofocus, Enter submits, Esc closes). The submit is gated by useCan on the
 * exact server capability (create vs update); the server re-checks regardless.
 *
 * On create the server validates code/libellé/discipline; on edit only libellé / seuil / statut are
 * mutable (the server schema), so the code/discipline fields are read-only in edit mode.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, FormField, Input, Modal, Select, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { useCreateReactif, useLabId, useUpdateReactif } from "../api";
import type { Reactif, UniteMesure } from "../types";
import { idStr } from "../types";
import { parseQty } from "../lib";

export interface ReactifFormModalProps {
  open: boolean;
  /** When set, edit this reagent; otherwise create a new one. */
  reactif?: Reactif | null;
  onClose: () => void;
  onSaved: () => void;
}

const UNITS: UniteMesure[] = ["test", "ml", "kit"];

export function ReactifFormModal({ open, reactif, onClose, onSaved }: ReactifFormModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const isEdit = !!reactif;
  const canCreate = useCan(INV_CAPS.reactifCreate);
  const canUpdate = useCan(INV_CAPS.reactifUpdate);
  const allowed = isEdit ? canUpdate : canCreate;

  const create = useCreateReactif(labId);
  const update = useUpdateReactif(labId);
  const pending = create.isPending || update.isPending;

  const [code, setCode] = useState("");
  const [libelle, setLibelle] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [automate, setAutomate] = useState("");
  const [unite, setUnite] = useState<UniteMesure>("test");
  const [seuil, setSeuil] = useState("");
  const [statut, setStatut] = useState<"actif" | "inactif">("actif");
  const [touched, setTouched] = useState(false);

  // Hydrate the form whenever the target reagent (or open) changes.
  useEffect(() => {
    if (!open) return;
    setTouched(false);
    if (reactif) {
      setCode(reactif.code);
      setLibelle(reactif.libelle);
      setDiscipline(reactif.discipline_code);
      setAutomate(reactif.automate_id != null ? idStr(reactif.automate_id) : "");
      setUnite((reactif.unite_mesure as UniteMesure) ?? "test");
      setSeuil(reactif.seuil_alerte != null ? String(reactif.seuil_alerte) : "");
      setStatut(reactif.statut === "inactif" ? "inactif" : "actif");
    } else {
      setCode("");
      setLibelle("");
      setDiscipline("");
      setAutomate("");
      setUnite("test");
      setSeuil("");
      setStatut("actif");
    }
  }, [open, reactif]);

  const codeError = touched && !isEdit && code.trim() === "" ? t("inventory.field.required") : null;
  const libelleError = touched && libelle.trim() === "" ? t("inventory.field.required") : null;
  const disciplineError =
    touched && !isEdit && discipline.trim() === "" ? t("inventory.field.required") : null;
  const seuilError = touched && seuil.trim() !== "" && parseQty(seuil) == null
    ? t("inventory.field.qtyInvalid")
    : null;

  const valid =
    libelle.trim() !== "" &&
    (isEdit || (code.trim() !== "" && discipline.trim() !== "")) &&
    (seuil.trim() === "" || parseQty(seuil) != null);

  const handleClose = () => {
    if (pending) return;
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !allowed) return;
    const seuilNum = seuil.trim() === "" ? undefined : parseQty(seuil) ?? undefined;

    if (isEdit && reactif) {
      update.mutate(
        {
          id: idStr(reactif.id),
          libelle: libelle.trim(),
          ...(seuilNum !== undefined ? { seuil_alerte: seuilNum } : {}),
          statut,
        },
        {
          onSuccess: () => {
            toast.success(t("inventory.reactif.edit.success"));
            onSaved();
          },
          onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
        },
      );
    } else {
      create.mutate(
        {
          code: code.trim(),
          libelle: libelle.trim(),
          discipline_code: discipline.trim(),
          ...(automate.trim() !== "" ? { automate_id: automate.trim() } : {}),
          unite_mesure: unite,
          ...(seuilNum !== undefined ? { seuil_alerte: seuilNum } : {}),
        },
        {
          onSuccess: () => {
            toast.success(t("inventory.reactif.create.success"));
            onSaved();
          },
          onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
        },
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t(isEdit ? "inventory.reactif.edit.title" : "inventory.reactif.create.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={pending}>
            {t("inventory.action.cancel")}
          </Button>
          <Button type="submit" form="inventory-reactif-form" loading={pending} disabled={!allowed || !valid}>
            {t(isEdit ? "inventory.reactif.edit.submit" : "inventory.reactif.create.submit")}
          </Button>
        </>
      }
    >
      <form id="inventory-reactif-form" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <FormField
            label={t("inventory.reactif.code")}
            hint={t("inventory.reactif.code.hint")}
            required={!isEdit}
            {...(codeError ? { error: codeError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                autoFocus={!isEdit}
                mono
                autoComplete="off"
                value={code}
                disabled={isEdit}
                invalid={!!codeError}
                placeholder="GLU-500"
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label={t("inventory.reactif.libelle")}
            required
            {...(libelleError ? { error: libelleError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                autoFocus={isEdit}
                value={libelle}
                invalid={!!libelleError}
                onChange={(e) => setLibelle(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label={t("inventory.reactif.discipline")}
            hint={t("inventory.reactif.discipline.hint")}
            required={!isEdit}
            {...(disciplineError ? { error: disciplineError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                mono
                autoComplete="off"
                value={discipline}
                disabled={isEdit}
                invalid={!!disciplineError}
                placeholder="BIOCH"
                onChange={(e) => setDiscipline(e.target.value.toUpperCase())}
              />
            )}
          </FormField>

          {!isEdit && (
            <FormField label={t("inventory.reactif.automate")} hint={t("inventory.reactif.automate.hint")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  autoComplete="off"
                  value={automate}
                  onChange={(e) => setAutomate(e.target.value)}
                />
              )}
            </FormField>
          )}

          {!isEdit && (
            <FormField label={t("inventory.reactif.unite")}>
              {(p) => (
                <Select
                  {...p}
                  value={unite}
                  onChange={(e) => setUnite(e.target.value as UniteMesure)}
                  options={UNITS.map((u) => ({ value: u, label: t(`inventory.unit.${u}`) }))}
                />
              )}
            </FormField>
          )}

          <FormField
            label={t("inventory.reactif.seuil")}
            hint={t("inventory.reactif.seuil.hint")}
            {...(seuilError ? { error: seuilError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="decimal"
                value={seuil}
                invalid={!!seuilError}
                placeholder="0"
                onChange={(e) => setSeuil(e.target.value)}
              />
            )}
          </FormField>

          {isEdit && (
            <FormField label={t("inventory.reactif.statut")}>
              {(p) => (
                <Select
                  {...p}
                  value={statut}
                  onChange={(e) => setStatut(e.target.value as "actif" | "inactif")}
                  options={[
                    { value: "actif", label: t("inventory.catalogue.statut.actif") },
                    { value: "inactif", label: t("inventory.catalogue.statut.inactif") },
                  ]}
                />
              )}
            </FormField>
          )}
        </div>
      </form>
    </Modal>
  );
}
